import Decimal from 'decimal.js'
import { isEqual, keys, values } from 'lodash-es'
import { Dictionary } from 'utilities/typeUtility/typeUtility'
import {
    OrderLineKdsStatus,
    OrderLineStatus,
    OrderLineContract,
    TableServiceOrderLineContract,
    OrderLineModifierProcessContract,
    DiscountType,
    OrderDiscountModel,
    OrderDiscountType,
    KitchenPrintStatus,
    OrderInventoryStatus,
} from 'models'
import {
    DiscountContractWithCustom,
    ItemMap,
    ItemViewModel,
    MenuViewModel,
    OrderItemViewModel,
    PriceListData,
    UsedItem,
    UsedItems,
    UsedModifier,
    UsedModifiers,
    usedModWithQuantity,
} from 'store/models/sellModels'
import {
    isFreeItem,
    isPromotionItem,
} from '../DiscountBogoDialog/hook/buyXgetYProcess'
import { isOpenItemId } from 'constant/magicNumber'
import Guid from 'utilities/Guid'
import { MENUSECTION_ALL_ITEMS } from 'views/Sale/utils/POSData/defaultPOSData'
import { LineModelExtend } from 'views/Sale/utils/orderCalculation/models/IOrderCalculationModel'
import { round } from 'views/Sale/utils/orderCalculation/common'
import { displayOrderLineItemName } from './orderContractUtils'
import dayjs from 'dayjs'

export const NONE_OPTION = '0'

/**
 * Check if ID is valid
 * There's use case because we introduce None value as '0' to support UX in modifier picker
 */
export function isValidId(id: string) {
    return id !== NONE_OPTION && id !== ''
}

export const NEW_ITEM_INFO = (): Omit<
    OrderItemViewModel,
    'item' | 'id' | 'amount' | 'lineTotal' | 'menuSectionId'
> => ({
    note: '',
    usedVariants: {},
    usedModifiers: {},
    usedItems: {},
    isSaved: false,
    kdsStatus: OrderLineKdsStatus.Unknown,
    kitchenPrintStatus: KitchenPrintStatus.NotPrintOrUndefined,
    status: OrderLineStatus.New,
    subtotal: new Decimal(0),
    orderID: undefined,
    assignTo: undefined,
    seatNote: null,
    createdOn: dayjs(),
    inventoryStatus: OrderInventoryStatus.None,
})

export function makeCustomDiscountId(itemId: string, index: number) {
    return `custom_discount_${itemId}_${index}`
}

export function getVariationItem(
    item: ItemViewModel,
    selectedVariantIds: string[],
) {
    if (item.variantGroups.length === selectedVariantIds.length) {
        const usedVariantsList = [...selectedVariantIds]
        usedVariantsList.sort()

        return (
            item.variations.find((i) => {
                if (!i.combinationIds) return false

                const combinationIds = [...i.combinationIds]
                combinationIds.sort()
                return isEqual(combinationIds, usedVariantsList)
            }) ?? item
        )
    }
    return item
}

export function changeBaseItemIDToVariantID(item: OrderItemViewModel) {
    if (Object.keys(item.usedVariants).length > 0) {
        const vItem = getVariationItem(item.item, values(item.usedVariants))
        if (vItem) {
            return {
                ...item,
                item: {
                    ...vItem,
                    // for order panel display and create modify dialog
                    name: item.item.name,
                    displayName: item.item.displayName,
                    variations: item.item.variations,
                    variantGroups: item.item.variantGroups,
                },
            }
        }
    }
    return item
}

export function getUsedItemsInComboChoice(
    usedItems: UsedItems,
    comboChoiceID: string,
) {
    return Object.keys(usedItems)
        .filter((pathToComboItem) => pathToComboItem.includes(comboChoiceID))
        .map((pathToComboItem) => usedItems[pathToComboItem])
}

export function countUsedItemInComboChoice(
    usedItems: UsedItems,
    comboChoiceID: string,
) {
    return getUsedItemsInComboChoice(usedItems, comboChoiceID).reduce(
        (acc, e) => acc + e.quantity,
        0,
    )
}

function isEqualUsedItem(a: UsedItem, b: UsedItem) {
    return (
        a.comboChoiceId === b.comboChoiceId &&
        a.nodeItemId === b.nodeItemId &&
        isEqual(a.usedVariants, b.usedVariants) &&
        isEqual(a.usedModifiers, b.usedModifiers)
    )
}

/** finding similar item to merge with targetComboItem. Return new UsedItems object */
export function tryGroupSimilarUsedItemWithTargetUsedItem(
    usedItems: UsedItems,
    targetComboItemPath: string,
) {
    const targetUsedItem = usedItems[targetComboItemPath]
    if (!targetUsedItem) return usedItems
    const matchUsedItemPath = Object.keys(usedItems)
        .filter((comboItemPath) => comboItemPath !== targetComboItemPath)
        .find((comboItemPath) =>
            isEqualUsedItem(targetUsedItem, usedItems[comboItemPath]),
        )
    if (matchUsedItemPath) {
        const newUsedItems = { ...usedItems }
        targetUsedItem.quantity += newUsedItems[matchUsedItemPath].quantity
        delete newUsedItems[matchUsedItemPath]

        return newUsedItems
    } else return usedItems
}

/*
    Compares the 'assignTo' lists of two items.
    Always returns true for QS.
*/
const compareAssignToLists = (
    lineItemA: OrderItemViewModel,
    lineItemB: OrderItemViewModel,
) => {
    // In the case of QS, lineItemA.assignTo will have a value if the order is saved, but lineItemB.assignTo is always undefined - so return true.
    if (lineItemB.assignTo === undefined) {
        return true
    }

    return isSameAssignTo(lineItemA, lineItemB)
}

function isAllSameDiscount(
    orderA: OrderItemViewModel,
    orderB: OrderItemViewModel,
) {
    const isSameDiscount = (
        a: DiscountContractWithCustom,
        b: DiscountContractWithCustom,
    ) => {
        return (
            a.discountID === b.discountID &&
            a.value === b.value &&
            a.type === b.type &&
            a.isCustomDiscount === b.isCustomDiscount &&
            a.discountRedemptionId === b.discountRedemptionId
        )
    }
    return (
        (orderA.discounts?.length ?? 0) === (orderB.discounts?.length ?? 0) &&
        (orderA.discounts?.every(
            (a) => orderB.discounts?.some((b) => isSameDiscount(a, b)) ?? false,
        ) ??
            true)
    )
}

function isSameAssignTo(
    orderA: OrderItemViewModel,
    orderB: OrderItemViewModel,
) {
    return isEqual(orderA.assignTo ?? [], orderB.assignTo ?? [])
}

export function isSameOrderItem(
    lineItemA: OrderItemViewModel,
    lineItemB: OrderItemViewModel,
) {
    // In case of duplicated items but be sent separately (KDS status can be different)
    // -> they should be different order lines in cart
    if (
        (lineItemA.kdsStatus !== OrderLineKdsStatus.Unknown ||
            lineItemB.kdsStatus !== OrderLineKdsStatus.Unknown) &&
        lineItemA.id !== lineItemB.id
    )
        return false

    const chainBool =
        (lineItemA.item.itemId === lineItemB.item.itemId ||
            lineItemA.item.itemId === lineItemB.item.baseItemId) &&
        isEqual(lineItemA.usedItems, lineItemB.usedItems) &&
        isEqual(lineItemA.usedModifiers, lineItemB.usedModifiers) &&
        isEqual(lineItemA.usedVariants, lineItemB.usedVariants) &&
        isEqual(
            lineItemA.kdsStatus ?? OrderLineKdsStatus.Unknown,
            lineItemB.kdsStatus ?? OrderLineKdsStatus.Unknown,
        ) &&
        lineItemA.note === lineItemB.note &&
        lineItemA.voidComp?.name === lineItemB.voidComp?.name &&
        // for Open Item, take an overridden name into account
        (lineItemA.displayName ?? '') === (lineItemB.displayName ?? '') &&
        compareAssignToLists(lineItemA, lineItemB) &&
        isEqual(lineItemA.seatNote, lineItemB.seatNote) &&
        lineItemA.inventoryStatus === lineItemB.inventoryStatus &&
        lineItemA.status === lineItemB.status

    if (isPromotionItem(lineItemA) || isPromotionItem(lineItemB)) {
        return isAllSameDiscount(lineItemA, lineItemB) && chainBool
    }
    return chainBool
}

export const isUseSamePriceList = (
    menus: MenuViewModel[],
    priceListData: PriceListData,
    itemA: OrderItemViewModel,
    itemB: OrderItemViewModel,
) => {
    const itemApriceListId =
        menus.find((m) => itemA.menuId && m.menuId === itemA.menuId)
            ?.priceListID ?? priceListData.basePriceList.priceListID
    const itemBpriceListId =
        menus.find((m) => itemB.menuId && m.menuId === itemB.menuId)
            ?.priceListID ?? priceListData.basePriceList.priceListID

    return itemApriceListId === itemBpriceListId
}

export function isSameUsedModifier(a: UsedModifier, b: UsedModifier) {
    if (a.modifierOptionId !== b.modifierOptionId) return false

    const aBindingIds = Object.keys(a.subModifierOptions).sort()
    const bBindingIds = Object.keys(b.subModifierOptions).sort()

    if (!isEqual(aBindingIds, bBindingIds)) return false

    return aBindingIds.every((bindingId) => {
        const aSubModifierOptionIdsInBinding =
            a.subModifierOptions[bindingId].sort()
        const bSubModifierOptionIdsInBinding =
            b.subModifierOptions[bindingId].sort()

        return isEqual(
            aSubModifierOptionIdsInBinding,
            bSubModifierOptionIdsInBinding,
        )
    })
}

interface flatModifierOption {
    modifierOptionId: string
    bindingId: string
    addPrice: number
}

export function subModifierPrices(usedModifiers: UsedModifiers) {
    const allSelectedModifierOptionIds = keys(usedModifiers)
        .flatMap((bindingItem) => {
            return usedModifiers[bindingItem].flatMap((parentOp) => {
                const mods: flatModifierOption[] = [
                    {
                        modifierOptionId: parentOp.modifierOptionId,
                        bindingId: bindingItem,
                        addPrice: parentOp.addPrice,
                    },
                ]
                const subMods: flatModifierOption[] = keys(
                    parentOp.subModifierOptions,
                ).flatMap((bindingMod) => {
                    return parentOp.subModifierOptions[
                        bindingMod
                    ].map<flatModifierOption>((opId) => {
                        return {
                            modifierOptionId: opId.modifierOptionId,
                            bindingId: bindingMod,
                            addPrice: opId.addPrice,
                        }
                    })
                })
                return mods.concat(subMods)
            })
        })
        .flat()
    let sum = new Decimal(0)
    allSelectedModifierOptionIds.forEach((mod) => {
        sum = sum.plus(mod.addPrice)
    })
    return sum
}

export const displayCartItemName = (line: OrderItemViewModel) => {
    return displayOrderLineItemName({
        item: line.item,
        itemID: line.item.itemId,
        sharedOrderLineID: line.isSubSharedOrderLine ? 'shared' : undefined,
        displayName: line.displayName,
    })
}

export function toTableOrderLineContract(
    orderItem: OrderItemViewModel,
    itemsMap: ItemMap,
    isSend?: boolean,
): TableServiceOrderLineContract {
    const lineContract = toOrderLineContract(
        orderItem,
        orderItem.status ?? OrderLineStatus.New,
        itemsMap,
    )
    if (!orderItem.assignTo || orderItem.assignTo.length === 0)
        throw new Error('`assignTo` is required in table service')

    return {
        orderLine: lineContract,
        isSend,
        targetOrderIDs: orderItem.assignTo ?? [],
    }
}

export function toQuickServiceOrderLineContract(
    orderItem: OrderItemViewModel,
    nextOrderLineStatus: OrderLineStatus,
    itemsMap: ItemMap,
    isSend?: boolean,
    taxLinesMap?: Dictionary<LineModelExtend>,
    defaultSortingIndex?: number,
): OrderLineContract {
    const isFree = isFreeItem(orderItem)
    const lineContract = toOrderLineContract(
        orderItem,
        nextOrderLineStatus,
        itemsMap,
    )
    const lineTax = taxLinesMap?.[orderItem.id]
    //for send flag for quick service only
    lineContract.isSendToKDS = isSend
    //for order line validation (QS only)
    lineContract.subtotal = round(
        new Decimal(orderItem.lineTotal).mul(orderItem.amount),
    ).toNumber()
    lineContract.totalTaxAmount = isFree
        ? 0
        : round(new Decimal(lineTax?.taxAmount ?? 0)).toNumber()
    lineContract.sortingIndex = lineTax?.sortingIndex ?? defaultSortingIndex
    lineContract.tempOrderLineID = orderItem.isSaved ? null : orderItem.id

    if (orderItem.kdsStatus !== OrderLineKdsStatus.Unknown) {
        lineContract.orderLineID = orderItem.id
    }
    return lineContract
}

function toOrderLineContract(
    orderItem: OrderItemViewModel,
    nextOrderLineStatus: OrderLineStatus,
    itemsMap: ItemMap,
) {
    let item = orderItem.item
    // in case variant item
    if (
        Object.keys(orderItem.usedItems).length === 0 &&
        orderItem.usedVariants
    ) {
        const vItem = getVariationItem(
            orderItem.item,
            values(orderItem.usedVariants),
        )
        if (vItem) item = vItem
    }

    const itemId = item.itemId
    const comboLineItems: OrderLineContract[] = getComboLine(
        orderItem,
        nextOrderLineStatus,
        itemsMap,
    )

    const modifierLines = getOrderLineModifiers(orderItem.usedModifiers)
    const orderLineContract: OrderLineContract = {
        orderLineID: Guid.isValid(orderItem.id) ? orderItem.id : undefined,

        itemID: itemId,
        menuID: orderItem?.menuId,
        menuSectionID:
            orderItem?.menuSectionId === MENUSECTION_ALL_ITEMS
                ? undefined
                : orderItem?.menuSectionId,
        orderQuantity: orderItem.amount,
        orderLineDiscountAmount: new Decimal(orderItem.itemDiscountAmount ?? 0)
            .plus(orderItem.lineCompDiscount ?? 0)
            .toNumber(),

        note: orderItem.note,
        orderLineModifiers: modifierLines,
        comboLineItems,
        status: nextOrderLineStatus,
        voidComp: orderItem.voidComp,
        // * : Attach discounts for Line-level
        discounts: (orderItem.discounts ?? []).map(
            (i): OrderDiscountModel => ({
                orderDiscountID: i.orderLineDiscountId,
                discountType: i.isCustomDiscount
                    ? OrderDiscountType.CustomDiscount
                    : OrderDiscountType.PredefinedDiscount,
                discountID: i.isCustomDiscount ? undefined : i.discountID,
                discountValue: i.value,
                isPercentage: i.type === DiscountType.Percentage,
                discountRedemptionID: i.discountRedemptionId,
            }),
        ),
        sharedOrderLineID: orderItem.sharedOrderLineID,
        isAutoAppliedDiscount: orderItem.isAutoAppliedDiscount ?? null,
        seatNote: orderItem.seatNote,
        createdOn: orderItem.createdOn.toISOString(),
    }

    // exclusive for "Open Item" line
    if (isOpenItemId(orderItem.item.itemId)) {
        orderLineContract.displayName = orderItem.displayName
        orderLineContract.price = orderItem.lineTotal
        orderLineContract.taxID = orderItem.overriddentaxID
        orderLineContract.isTaxInclusive = orderItem.overriddenIsTaxInclusive
    }

    return orderLineContract
}

function getComboLine(
    orderItem: OrderItemViewModel,
    nextOrderLineStatus: OrderLineStatus,
    itemsMap: ItemMap,
) {
    return keys(orderItem.usedItems).map((path) => {
        const cLine: OrderLineContract = {
            createdOn: orderItem.createdOn.toISOString(),
        }

        const modLines = getOrderLineModifiers(
            orderItem.usedItems[path].usedModifiers,
        )

        const choiceId = orderItem.usedItems[path].comboChoiceId

        const itemVariantOptions = values(
            orderItem.usedItems[path].usedVariants,
        )

        const baseItemId = orderItem.usedItems[path].nodeItemId
        if (itemVariantOptions.length > 0) {
            // resolve to variant item ID
            // note that usedItems[path].nodeItemId is base item id
            const variantItem = getVariationItem(
                itemsMap[baseItemId]!,
                itemVariantOptions,
            )
            cLine.itemID = variantItem?.itemId
        } else cLine.itemID = baseItemId
        cLine.comboChoiceID = choiceId
        cLine.orderLineVariantOptionIds = itemVariantOptions
        cLine.orderLineModifiers = modLines
        cLine.orderQuantity = orderItem.usedItems[path].quantity
        cLine.status = nextOrderLineStatus

        return cLine
    })
}

export function getOrderLineModifiers(usedModifiers: UsedModifiers) {
    return keys(usedModifiers).flatMap((parentBinding) => {
        const modWithQuantity = usedModifiers[parentBinding].reduce<
            usedModWithQuantity[]
        >((newMod, cur) => {
            const existMod = newMod.find(
                (mod) =>
                    mod.modifierOptionId === cur.modifierOptionId &&
                    isEqual(mod.subModifierOptions, cur.subModifierOptions),
            )
            if (existMod) existMod.quantity++
            else
                newMod.push({
                    ...cur,
                    quantity: 1,
                })
            return newMod
        }, [])
        return modWithQuantity.flatMap((parentMod) => {
            const modLine: OrderLineModifierProcessContract = {
                modifierOptionID: parentMod.modifierOptionId,
                quantity: parentMod.quantity,
                priceAdd: parentMod.addPrice ?? 0,
                bindingModifierGroupID: parentBinding,
                subModifiers: [],
            }
            modLine.subModifiers = keys(parentMod.subModifierOptions).flatMap(
                (binding) => {
                    return parentMod.subModifierOptions[binding]
                        .filter((op) => op.modifierOptionId !== NONE_OPTION)
                        .reduce<usedModWithQuantity[]>((newMod, cur) => {
                            const existMod = newMod.find(
                                (mod) =>
                                    mod.modifierOptionId ===
                                    cur.modifierOptionId,
                            )
                            if (existMod) existMod.quantity++
                            else
                                newMod.push({
                                    modifierGroupId: cur.modifierGroupId,
                                    modifierOptionId: cur.modifierOptionId,
                                    addPrice: cur.addPrice,
                                    quantity: 1,
                                    subModifierOptions: {},
                                })
                            return newMod
                        }, [])
                        .map<OrderLineModifierProcessContract>((s) => {
                            return {
                                modifierOptionID: s.modifierOptionId,
                                priceAdd: s.addPrice,
                                quantity: s.quantity,
                                bindingModifierGroupID: binding,
                                subModifiers: [],
                            }
                        })
                },
            )
            return modLine
        })
    })
}
